import { useCallback, useEffect, useRef, useState } from "react";
import { Check, Copy, X } from "lucide-react";
import { ANDREA_PHONE_DISPLAY, CONTACT_FALLBACK_EVENT } from "../lib/contact";

// Desktop-only companion to the sms:/tel: CTAs. On devices without native
// messaging/dialer handlers, contactClick() intercepts the click and this
// quiet on-brand card shows Andrea's number with a copy action instead of a
// dead link. Mobile never sees it — native behavior is untouched there.
// Interaction-only: showing this card follows the same call_andrea_click /
// text_andrea_click event the link already fired — nothing more is tracked.
const COPY = {
  text: {
    label: "Text Andrea",
    note: "On your phone, this link opens your messaging app with Andrea's number already filled in. From a computer, copy her number and text her whenever you're ready.",
  },
  call: {
    label: "Call Andrea",
    note: "On your phone, this link opens your dialer with Andrea's number ready to call. From a computer, copy her number and call whenever it suits you.",
  },
};

export const ContactFallback = () => {
  const [kind, setKind] = useState(null);
  const [copied, setCopied] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const onShow = (e) => {
      setKind(e.detail?.kind === "call" ? "call" : "text");
      setCopied(false);
    };
    window.addEventListener(CONTACT_FALLBACK_EVENT, onShow);
    return () => window.removeEventListener(CONTACT_FALLBACK_EVENT, onShow);
  }, []);

  const close = useCallback(() => setKind(null), []);

  useEffect(() => {
    if (!kind) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    const onDown = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target)) close();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [kind, close]);

  if (!kind) return null;
  const copy = COPY[kind];

  const copyNumber = async () => {
    try {
      await navigator.clipboard.writeText(ANDREA_PHONE_DISPLAY);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = ANDREA_PHONE_DISPLAY;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopied(true);
  };

  return (
    <div
      ref={cardRef}
      role="dialog"
      aria-label={`${copy.label} at ${ANDREA_PHONE_DISPLAY}`}
      data-testid="contact-fallback-popover"
      className="fixed bottom-6 right-6 z-[80] w-[calc(100vw-3rem)] max-w-sm rounded-[1.6rem] border border-ink/10 bg-ivory shadow-[0_24px_60px_-18px_rgba(36,28,19,0.45)] p-7"
    >
      <button
        data-testid="contact-fallback-close"
        onClick={close}
        aria-label="Close"
        className="absolute top-4 right-4 p-1 text-smoke hover:text-ink transition-colors duration-300"
      >
        <X size={16} />
      </button>
      <p className="text-[10px] tracking-[0.25em] uppercase text-euca font-semibold">{copy.label}</p>
      <p data-testid="contact-fallback-number" className="mt-3 font-serif font-light text-3xl text-ink tracking-tight">
        {ANDREA_PHONE_DISPLAY}
      </p>
      <p data-testid="contact-fallback-note" className="mt-3 text-sm leading-relaxed text-smoke">
        {copy.note}
      </p>
      <button
        data-testid="contact-fallback-copy"
        onClick={copyNumber}
        className="mt-5 inline-flex items-center gap-2 rounded-full bg-ink text-ivory text-sm font-medium px-6 py-3 hover:bg-espresso transition-colors duration-300"
      >
        {copied ? <Check size={15} aria-hidden="true" /> : <Copy size={15} aria-hidden="true" />}
        {copied ? "Copied" : "Copy number"}
      </button>
    </div>
  );
};
